import {useCallback, useEffect, useMemo, useState} from 'react';
import {Link, NavLink, useHistory, useLocation} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {useMetaTags} from '../../services/useMetaTags';
import trackPageView from '../../services/trackPageView';
import AdvertiseBlock from '../../components/AdvertiseBlock';
import '../../assets/styles/trainingCourse/coursesPage.scss';

const PER_PAGE = 12;
const SORT_VALUES = ['popular', 'newest', 'score'];

function normalizeUrl(value) {
    if (!value) {
        return '/img/fallback-min.png';
    }

    if (/^(https?:)?\/\//.test(value)) {
        return value;
    }

    return value.startsWith('/') ? value : `/${value}`;
}

function resolveLocalePrefix(pathname) {
    const match = pathname.match(/^\/(en|uk)(?=\/|$)/);

    return match ? `/${match[1]}` : '';
}

function resolveBooleanParam(value) {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'number') {
        return value > 0;
    }

    if (typeof value !== 'string') {
        return false;
    }

    return ['1', 'true', 'yes', 'on'].includes(value.trim().toLowerCase());
}

function parsePositiveInt(value, fallback = 1) {
    const parsed = Number(value);

    if (!Number.isFinite(parsed) || parsed < 1) {
        return fallback;
    }

    return Math.floor(parsed);
}

function getDefaultFilters() {
    return {
        categories: [],
        complexity: [],
        hasCertificate: false,
        type: [],
        isFree: false,
        onlyMySpecialities: false,
    };
}

function parseUniqueIntegerArray(values) {
    const unique = new Set();

    values.forEach((value) => {
        const parsed = Number(value);
        if (Number.isInteger(parsed) && parsed > 0) {
            unique.add(parsed);
        }
    });

    return Array.from(unique);
}

function parseUniqueStringArray(values, allowedValues = []) {
    const allowed = new Set(allowedValues);
    const unique = new Set();

    values.forEach((value) => {
        const normalized = String(value || '').trim();
        if (normalized === '') {
            return;
        }

        if (allowed.size > 0 && !allowed.has(normalized)) {
            return;
        }

        unique.add(normalized);
    });

    return Array.from(unique);
}

function resolveSortValue(value) {
    const normalized = String(value || '').trim();

    if (SORT_VALUES.includes(normalized)) {
        return normalized;
    }

    return 'popular';
}

function parseQueryState(search, {isAllTab, hasTypeFilter}) {
    const params = new URLSearchParams(search);
    const filters = getDefaultFilters();

    if (isAllTab) {
        filters.categories = parseUniqueIntegerArray(params.getAll('categories'));
        filters.complexity = parseUniqueIntegerArray(params.getAll('complexity'));
        filters.hasCertificate = resolveBooleanParam(params.get('hasCertificate'));
        filters.isFree = resolveBooleanParam(params.get('isFree'));
        filters.onlyMySpecialities = resolveBooleanParam(params.get('onlyMySpecialities'));

        if (hasTypeFilter) {
            filters.type = parseUniqueStringArray(params.getAll('type'), ['training', 'commercial']);
        }

        if (filters.hasCertificate && hasTypeFilter) {
            filters.type = filters.type.filter((value) => value !== 'commercial');
        }

        if (filters.type.includes('commercial')) {
            filters.hasCertificate = false;
        }
    }

    return {
        page: parsePositiveInt(params.get('page'), 1),
        sort: resolveSortValue(params.get('sort')),
        filters,
    };
}

function buildUiQueryString({page, sort, filters, isAllTab, hasTypeFilter}) {
    const params = new URLSearchParams();

    if (page > 1) {
        params.set('page', String(page));
    }

    if (sort !== 'popular') {
        params.set('sort', sort);
    }

    if (isAllTab) {
        filters.categories.forEach((value) => params.append('categories', String(value)));
        filters.complexity.forEach((value) => params.append('complexity', String(value)));

        if (filters.hasCertificate) {
            params.set('hasCertificate', '1');
        }

        if (filters.isFree) {
            params.set('isFree', '1');
        }

        if (filters.onlyMySpecialities) {
            params.set('onlyMySpecialities', '1');
        }

        if (hasTypeFilter) {
            filters.type.forEach((value) => params.append('type', value));
        }
    }

    return params.toString();
}

function buildRequestQuery({page, sort, filters, isAllTab, hasTypeFilter}) {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('perPage', String(PER_PAGE));
    params.set('sort', sort);

    if (isAllTab) {
        filters.categories.forEach((value) => params.append('categories', String(value)));
        filters.complexity.forEach((value) => params.append('complexity', String(value)));

        if (filters.hasCertificate) {
            params.set('hasCertificate', '1');
        }

        if (filters.isFree) {
            params.set('isFree', '1');
        }

        if (filters.onlyMySpecialities) {
            params.set('onlyMySpecialities', '1');
        }

        if (hasTypeFilter) {
            filters.type.forEach((value) => params.append('type', value));
        }
    }

    return params.toString();
}

function buildPaginationItems(currentPage, totalPages, maxVisible = 7) {
    if (totalPages <= maxVisible) {
        return Array.from({length: totalPages}, (_, index) => index + 1);
    }

    const pages = [1];
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    if (start > 2) {
        pages.push('ellipsis-left');
    }

    for (let page = start; page <= end; page += 1) {
        pages.push(page);
    }

    if (end < totalPages - 1) {
        pages.push('ellipsis-right');
    }

    pages.push(totalPages);

    return pages;
}

function getComplexityText(complexityValue, textNamespace, t) {
    if (complexityValue === 1) {
        return t(`${textNamespace}.filters.complexity.low`, t('courses.filters.complexity.low'));
    }

    if (complexityValue === 2) {
        return t(`${textNamespace}.filters.complexity.medium`, t('courses.filters.complexity.medium'));
    }

    return t(`${textNamespace}.filters.complexity.high`, t('courses.filters.complexity.high'));
}

function normalizeCurrencyCode(value) {
    return String(value || '').trim().toLowerCase();
}

function normalizeCertificateType(value) {
    return String(value || '').trim().toLowerCase();
}

function hasCertificateTemplate(item) {
    const template = item?.certificateTemplate;
    if (!template) {
        return false;
    }

    if (typeof template !== 'object') {
        return true;
    }

    return Boolean(template.id || template.name);
}

function shouldShowCertificate(item) {
    if (hasCertificateTemplate(item)) {
        return true;
    }

    return resolveBooleanParam(item?.hasCertificate);
}

function formatNumericValue(value) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
        return null;
    }

    if (Number.isInteger(numeric)) {
        return String(numeric);
    }

    return String(numeric).replace(/\.0+$/, '').replace(/(\.\d*[1-9])0+$/, '$1');
}

function resolveCertificateLabel(item, textNamespace, t) {
    if (!shouldShowCertificate(item)) {
        return null;
    }

    const defaultLabel = t(`${textNamespace}.item.withCertificate`, t('courses.item.withCertificate'));
    const certificateType = normalizeCertificateType(item?.certificateType);
    const certificateScoreRaw = item?.certificateScore ?? item?.score;
    const certificateScoreNumeric = Number(certificateScoreRaw);
    const formattedCertificateScore = formatNumericValue(certificateScoreRaw);

    if (certificateType === 'diploma') {
        return t(`${textNamespace}.item.withDiploma`, t('courses.item.withDiploma', defaultLabel));
    }

    if (certificateType === 'certificatebpr') {
        const bprLabel = t(`${textNamespace}.item.withCertificateBpr`, t('courses.item.withCertificateBpr', defaultLabel));

        if (Number.isFinite(certificateScoreNumeric) && certificateScoreNumeric > 0 && formattedCertificateScore) {
            return `${bprLabel} · ${formattedCertificateScore} ${t('app.currencies.cme')}`;
        }

        return bprLabel;
    }

    return defaultLabel;
}

function formatPriceLabel(item, t) {
    const costs = Array.isArray(item.cost) ? item.cost : [];

    if (costs.length > 0) {
        return costs
            .map((cost) => `${cost.value} ${t(`app.currencies.${String(cost.currencyCode || '').toLowerCase()}`)}`)
            .join(' · ');
    }

    const pricePolicyFinalPrice = Number(item?.pricePolicy?.finalPrice);
    const bonusPrice = Number(item?.bonusPrice);
    const fallbackPrices = [];

    if (Number.isFinite(pricePolicyFinalPrice) && pricePolicyFinalPrice > 0) {
        fallbackPrices.push(`${item.pricePolicy.finalPrice} ${t('app.currencies.uah')}`);
    }

    if (Number.isFinite(bonusPrice) && bonusPrice > 0) {
        fallbackPrices.push(`${item.bonusPrice} ${t('app.currencies.bon')}`);
    }

    if (fallbackPrices.length > 0) {
        return fallbackPrices.join(' · ');
    }

    return null;
}

function formatRewardLabel(item, t) {
    const rewards = Array.isArray(item.prices) ? item.prices : [];
    const rewardCurrencyCodes = new Set(['soc', 'edu', 'bon']);

    let rewardItems = rewards
        .map((price) => {
            const code = normalizeCurrencyCode(price?.code || price?.currencyCode || price?.currency?.code);
            if (!rewardCurrencyCodes.has(code)) {
                return null;
            }

            const rawValue = price?.price ?? price?.value ?? price?.amount ?? '';
            const formattedValue = formatNumericValue(rawValue);
            if (!formattedValue) {
                return null;
            }

            return {
                code,
                numericValue: Number(rawValue),
                formattedValue,
            };
        })
        .filter(Boolean);

    if (normalizeCertificateType(item?.certificateType) === 'certificatebpr' && shouldShowCertificate(item)) {
        const certificateScoreRaw = item?.certificateScore ?? item?.score;
        const certificateScoreNumeric = Number(certificateScoreRaw);
        if (Number.isFinite(certificateScoreNumeric) && certificateScoreNumeric > 0) {
            rewardItems = rewardItems.filter((rewardItem) => (
                rewardItem.code !== 'soc'
                || !Number.isFinite(rewardItem.numericValue)
                || rewardItem.numericValue !== certificateScoreNumeric
            ));
        }
    }

    const parts = rewardItems.map((rewardItem) => `${rewardItem.formattedValue} ${t(`app.currencies.${rewardItem.code}`)}`);

    if (parts.length === 0) {
        return null;
    }

    return parts.join(' / ');
}

function buildCardBackground(image) {
    const normalizedImage = normalizeUrl(image);

    return `linear-gradient(180deg, rgba(0,0,0,.18), rgba(0,0,0,.58)), url('${normalizedImage}')`;
}

function TrainingCourseCard({item, isPinned = false, isTesting, t}) {
    const textNamespace = isTesting ? 'testings' : 'courses';
    const previewUrl = isTesting ? `/testings/preview/${item.id}` : `/course/preview/${item.id}`;
    const complexityValue = Number(item.complexity || 1);
    const complexityText = getComplexityText(complexityValue, textNamespace, t);
    const priceLabel = formatPriceLabel(item, t);
    const rewardLabel = formatRewardLabel(item, t);
    const certificateLabel = resolveCertificateLabel(item, textNamespace, t);
    const resolvedPriceLabel = priceLabel || t(`${textNamespace}.list.chips.free`, t('courses.list.chips.free', 'Free'));
    const isFreePrice = !priceLabel;

    return (
        <article className="course-card" data-state="all">
            <Link
                className="course-card__media"
                to={previewUrl}
                style={{backgroundImage: buildCardBackground(item.image)}}>
                <span className={`badge badge--price course-card__price-chip ${isFreePrice ? 'badge--free-price' : ''}`}>{resolvedPriceLabel}</span>
                <div className="course-card__overlay">
                    <div className="course-card__kicker">
                        {isPinned ? t(`${textNamespace}.item.top`, t('courses.item.top')) : t(`${textNamespace}.title`, t('courses.title'))}
                    </div>
                    <div className="course-card__title">{item.title}</div>
                </div>
            </Link>

            <div className="course-card__body">
                <p className="course-card__desc">{item.shortDescription}</p>

                <div className="course-card__footer">
                    <div className="course-card__stats">
                        <span className="stat"><span className="stat__ic">▦</span><span className="stat__val">{complexityText}</span></span>
                        {!!rewardLabel && <span className="stat"><span className="stat__ic">●</span><span className="stat__val">{rewardLabel}</span></span>}
                        {!!certificateLabel && (
                            <span className="stat"><span className="stat__ic">🎓</span><span className="stat__val">{certificateLabel}</span></span>
                        )}
                    </div>
                </div>
            </div>
        </article>
    );
}

function UserTrainingCourseCard({
    item,
    isTesting,
    isFinishedTab,
    localePrefix,
    t,
    isAddedToProfile,
    isAddToProfilePending,
    onAddToProfile,
    onCertificateUnavailable,
}) {
    const history = useHistory();
    const textNamespace = isTesting ? 'testings' : 'courses';
    const courseUrl = isTesting ? `/testings/${item.id}` : `/course/${item.id}`;
    const complexityValue = Number(item.complexity || 1);
    const complexityText = getComplexityText(complexityValue, textNamespace, t);
    const rewardLabel = formatRewardLabel(item, t);
    const certificateLabel = resolveCertificateLabel(item, textNamespace, t);
    const canShowCertificateAction = shouldShowCertificate(item);
    const addedToProfile = typeof isAddedToProfile === 'function' ? isAddedToProfile(item.id) : !!isAddedToProfile;
    const addToProfilePending = typeof isAddToProfilePending === 'function' ? isAddToProfilePending(item.id) : !!isAddToProfilePending;
    const rawProgress = Math.max(0, Math.min(100, Number(item.completenessPercents) || 0));
    const progressPercent = isFinishedTab ? 100 : rawProgress;

    const handleCertificateClick = () => {
        if (item.allowToDownloadCertificate) {
            history.push(`${localePrefix}/course/${item.id}/certificate`);
            return;
        }

        onCertificateUnavailable();
    };

    return (
        <article className="course-card" data-state={isFinishedTab ? 'done' : 'mine'}>
            <Link
                className={`course-card__media ${!isFinishedTab ? 'course-card__media--with-progress' : ''}`}
                to={courseUrl}
                style={{backgroundImage: buildCardBackground(item.image)}}>
                <div className="course-card__overlay">
                    <div className="course-card__kicker">
                        {isFinishedTab
                            ? t(`${textNamespace}.list.badges.done`, t('courses.list.badges.done', 'Completed'))
                            : t(`${textNamespace}.my.title`, t('courses.my.title'))}
                    </div>
                    <div className="course-card__title">{item.title}</div>
                </div>

                {!isFinishedTab && (
                    <div className="course-card__progress" aria-label={`Progress ${Math.round(progressPercent)}%`}>
                        <div className="course-card__progressbar" style={{width: `${progressPercent}%`}}></div>
                    </div>
                )}

                {isFinishedTab && (
                    <div className="course-card__stamp">✔ {t(`${textNamespace}.list.badges.done`, t('courses.list.badges.done', 'Completed'))}</div>
                )}
            </Link>

            <div className="course-card__body">
                <p className="course-card__desc">{item.shortDescription}</p>

                {!!isFinishedTab && item.dateStarted && item.dateFinished && (
                    <p className="course-card__period">
                        {t(`${textNamespace}.finished.studyPeriod`, t('courses.finished.studyPeriod'))} {item.dateStarted} - {item.dateFinished}
                    </p>
                )}

                <div className="course-card__footer">
                    <div className="course-card__stats">
                        <span className="stat"><span className="stat__ic">▦</span><span className="stat__val">{complexityText}</span></span>
                        {!isFinishedTab && <span className="stat"><span className="stat__ic">▶</span><span className="stat__val">{Math.round(progressPercent)}%</span></span>}
                        {!!rewardLabel && <span className="stat"><span className="stat__ic">●</span><span className="stat__val">{rewardLabel}</span></span>}
                        {!!certificateLabel && (
                            <span className="stat"><span className="stat__ic">🎓</span><span className="stat__val">{certificateLabel}</span></span>
                        )}
                    </div>
                </div>

                {isFinishedTab && (
                    <div className="course-card__actions">
                        {canShowCertificateAction && (
                            <button type="button" className="am-btn" onClick={handleCertificateClick}>
                                {t(`${textNamespace}.finished.viewCertificate`, t('courses.finished.viewCertificate'))}
                            </button>
                        )}
                        <button
                            type="button"
                            className="am-btn"
                            disabled={addedToProfile || addToProfilePending}
                            onClick={() => onAddToProfile(item.id)}>
                            {addedToProfile
                                ? t(`${textNamespace}.finished.addedToSummary`, t('courses.finished.addedToSummary'))
                                : t(`${textNamespace}.finished.addToSummary`, t('courses.finished.addToSummary'))}
                        </button>
                    </div>
                )}
            </div>
        </article>
    );
}

export default function TrainingCourseListPage({isTesting = false, tab = 'all'}) {
    const {t} = useTranslation();
    const history = useHistory();
    const {pathname, search} = useLocation();

    const isAllTab = tab === 'all';
    const isFinishedTab = tab === 'finished';
    const hasTypeFilter = !isTesting;
    const textNamespace = isTesting ? 'testings' : 'courses';
    const localePrefix = useMemo(() => resolveLocalePrefix(pathname), [pathname]);
    const basePathRaw = isTesting ? '/testings' : '/courses';
    const myTabPathRaw = isTesting ? '/my-testings' : '/mycourses';
    const finishedTabPathRaw = isTesting ? '/finished-testings' : '/finished-courses';
    const basePath = `${localePrefix}${basePathRaw}`;
    const myTabPath = `${localePrefix}${myTabPathRaw}`;
    const finishedTabPath = `${localePrefix}${finishedTabPathRaw}`;

    const listEndpoint = useMemo(() => {
        if (isTesting) {
            if (tab === 'my') {
                return '/api/v1/testings/my';
            }

            if (tab === 'finished') {
                return '/api/v1/testings/finished';
            }

            return '/api/v1/testings';
        }

        if (tab === 'my') {
            return '/api/v1/courses/my';
        }

        if (tab === 'finished') {
            return '/api/v1/courses/finished';
        }

        return '/api/v1/courses';
    }, [isTesting, tab]);

    const metaEndpoint = isTesting ? '/api/v1/testings/meta' : '/api/v1/courses/meta';
    const listKey = isTesting ? 'testings' : 'courses';
    const topAdvertiseUnit = isAllTab ? 'courses1' : 'myCourses1';
    const bottomAdvertiseUnit = isAllTab ? 'courses5' : 'myCourses2';

    const pageTitle = useMemo(() => {
        if (isFinishedTab) {
            return t(`${textNamespace}.finished.title`, t(`${textNamespace}.tabs.finished`));
        }

        if (tab === 'my') {
            return t(`${textNamespace}.my.title`, t(`${textNamespace}.tabs.my`));
        }

        return isTesting ? t('testings.title', 'Testings') : t('courses.title');
    }, [isFinishedTab, isTesting, t, tab, textNamespace]);

    const [meta, setMeta] = useState({
        categories: [],
        hasMyCourses: false,
        tabCounters: {
            all: 0,
            my: 0,
            finished: 0,
        },
        loaded: false,
    });
    const [draftFilters, setDraftFilters] = useState(getDefaultFilters());
    const [listData, setListData] = useState({
        items: [],
        pinnedItems: [],
        pagination: {
            page: 1,
            perPage: PER_PAGE,
            totalItems: 0,
            totalPages: 0,
            hasMore: false,
        },
        loading: false,
        loaded: false,
    });
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [showVerifyPopup, setShowVerifyPopup] = useState(false);
    const [addedToProfileIds, setAddedToProfileIds] = useState({});
    const [addToProfilePendingIds, setAddToProfilePendingIds] = useState({});

    const queryState = useMemo(
        () => parseQueryState(search, {isAllTab, hasTypeFilter}),
        [search, isAllTab, hasTypeFilter]
    );

    useMetaTags({
        title: pageTitle,
        description: t('app.description'),
        metas: [
            {name: 'keywords', content: t('app.keywords')},
        ],
    });

    useEffect(() => {
        trackPageView(pageTitle);
    }, [pageTitle]);

    useEffect(() => {
        setDraftFilters(queryState.filters);
    }, [queryState.filters, tab, isTesting]);

    useEffect(() => {
        setAddedToProfileIds({});
        setAddToProfilePendingIds({});
    }, [tab, isTesting]);

    const applyQueryState = useCallback((nextState, replace = false) => {
        const nextSearch = buildUiQueryString({
            page: nextState.page,
            sort: nextState.sort,
            filters: nextState.filters,
            isAllTab,
            hasTypeFilter,
        });
        const nextUrl = `${pathname}${nextSearch ? `?${nextSearch}` : ''}`;

        if (replace) {
            history.replace(nextUrl);
            return;
        }

        history.push(nextUrl);
    }, [hasTypeFilter, history, isAllTab, pathname]);

    useEffect(() => {
        setListData((current) => ({...current, loading: true}));

        const requestQuery = buildRequestQuery({
            page: queryState.page,
            sort: queryState.sort,
            filters: queryState.filters,
            isAllTab,
            hasTypeFilter,
        });

        fetch(`${listEndpoint}?${requestQuery}`, {
            credentials: 'same-origin',
        })
            .then(async (response) => {
                if (response.status === 401) {
                    return null;
                }

                return response.json();
            })
            .then((result) => {
                if (!result) {
                    setListData({
                        items: [],
                        pinnedItems: [],
                        pagination: {
                            page: 1,
                            perPage: PER_PAGE,
                            totalItems: 0,
                            totalPages: 0,
                            hasMore: false,
                        },
                        loading: false,
                        loaded: true,
                    });
                    return;
                }

                const resultItems = result[listKey] || [];
                const pagination = result.pagination || {
                    page: queryState.page,
                    perPage: PER_PAGE,
                    totalItems: resultItems.length,
                    totalPages: 1,
                    hasMore: false,
                };

                setListData({
                    items: resultItems,
                    pinnedItems: isAllTab ? (result.pinnedCourses || []) : [],
                    pagination,
                    loading: false,
                    loaded: true,
                });
            })
            .catch(() => {
                setListData((current) => ({
                    ...current,
                    loading: false,
                    loaded: true,
                }));
            });
    }, [hasTypeFilter, isAllTab, listEndpoint, listKey, queryState.filters, queryState.page, queryState.sort]);

    useEffect(() => {
        fetch(metaEndpoint, {
            credentials: 'same-origin',
        })
            .then(async (response) => {
                if (response.status === 401) {
                    return null;
                }

                return response.json();
            })
            .then((result) => {
                if (!result) {
                    setMeta({
                        categories: [],
                        hasMyCourses: false,
                        tabCounters: {
                            all: 0,
                            my: 0,
                            finished: 0,
                        },
                        loaded: true,
                    });
                    return;
                }

                setMeta({
                    categories: result.categories || [],
                    hasMyCourses: !!result.hasMyCourses,
                    tabCounters: {
                        all: Number(result?.tabCounters?.all || 0),
                        my: Number(result?.tabCounters?.my || 0),
                        finished: Number(result?.tabCounters?.finished || 0),
                    },
                    loaded: true,
                });
            })
            .catch(() => {
                setMeta((current) => ({...current, loaded: true}));
            });
    }, [metaEndpoint]);

    const toggleDraftArrayFilter = (key, value) => {
        setDraftFilters((current) => {
            const hasValue = current[key].includes(value);

            return {
                ...current,
                [key]: hasValue
                    ? current[key].filter((item) => item !== value)
                    : [...current[key], value],
            };
        });
    };

    const toggleDraftHasCertificate = (value) => {
        setDraftFilters((current) => {
            const next = {
                ...current,
                hasCertificate: value,
            };

            if (value) {
                next.type = current.type.filter((item) => item !== 'commercial');
            }

            return next;
        });
    };

    const toggleDraftType = (value, checked) => {
        setDraftFilters((current) => {
            const nextType = checked
                ? [...current.type, value]
                : current.type.filter((item) => item !== value);
            const next = {
                ...current,
                type: Array.from(new Set(nextType)),
            };

            if (next.type.includes('commercial')) {
                next.hasCertificate = false;
            }

            return next;
        });
    };

    const closeFilter = () => setIsFilterOpen(false);
    const openFilter = () => setIsFilterOpen(true);

    const handleSortChange = (event) => {
        const nextSort = resolveSortValue(event.target.value);
        applyQueryState({
            ...queryState,
            sort: nextSort,
            page: 1,
        });
    };

    const applyFilters = () => {
        applyQueryState({
            page: 1,
            sort: queryState.sort,
            filters: draftFilters,
        });
        closeFilter();
    };

    const resetFilters = () => {
        const clearedFilters = getDefaultFilters();
        setDraftFilters(clearedFilters);

        applyQueryState({
            page: 1,
            sort: queryState.sort,
            filters: clearedFilters,
        });
    };

    const handleQuickChipClick = (chip) => {
        const nextFilters = {
            ...queryState.filters,
        };

        if (chip === 'all') {
            nextFilters.isFree = false;
            nextFilters.hasCertificate = false;
            nextFilters.onlyMySpecialities = false;
        }

        if (chip === 'free') {
            nextFilters.isFree = true;
            nextFilters.hasCertificate = false;
            nextFilters.onlyMySpecialities = false;
        }

        if (chip === 'certificate') {
            nextFilters.isFree = false;
            nextFilters.hasCertificate = true;
            nextFilters.onlyMySpecialities = false;
            if (hasTypeFilter) {
                nextFilters.type = nextFilters.type.filter((value) => value !== 'commercial');
            }
        }

        if (chip === 'speciality') {
            nextFilters.isFree = false;
            nextFilters.hasCertificate = false;
            nextFilters.onlyMySpecialities = true;
        }

        setDraftFilters(nextFilters);
        applyQueryState({
            ...queryState,
            filters: nextFilters,
            page: 1,
        });
    };

    const activeQuickChip = useMemo(() => {
        if (queryState.filters.onlyMySpecialities) {
            return 'speciality';
        }

        if (queryState.filters.isFree) {
            return 'free';
        }

        if (queryState.filters.hasCertificate) {
            return 'certificate';
        }

        return 'all';
    }, [queryState.filters.hasCertificate, queryState.filters.isFree, queryState.filters.onlyMySpecialities]);

    const handleAddToProfile = (courseId) => {
        if (!courseId || addToProfilePendingIds[courseId]) {
            return;
        }

        setAddToProfilePendingIds((current) => ({...current, [courseId]: true}));
        fetch(`/api/v1/course/${courseId}/profile`, {
            method: 'POST',
            credentials: 'same-origin',
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error('Failed to add course to profile');
                }

                setAddedToProfileIds((current) => ({...current, [courseId]: true}));
            })
            .catch(() => null)
            .finally(() => {
                setAddToProfilePendingIds((current) => ({
                    ...current,
                    [courseId]: false,
                }));
            });
    };

    const noItemsFound = listData.loaded
        && !listData.loading
        && listData.items.length === 0
        && listData.pinnedItems.length === 0;
    const disableCertificateFilter = hasTypeFilter && draftFilters.type.includes('commercial');
    const disableCommercialType = hasTypeFilter && draftFilters.hasCertificate;
    const noItemsMessage = isFinishedTab
        ? t(`${textNamespace}.finished.noCourses`, t('courses.finished.noCourses'))
        : t(`${textNamespace}.noCourses`, t('courses.noCourses'));

    const totalFound = (listData.pagination?.totalItems || 0) + (isAllTab ? listData.pinnedItems.length : 0);
    const tabCounters = meta.tabCounters || {all: 0, my: 0, finished: 0};
    const paginationItems = buildPaginationItems(
        listData.pagination?.page || queryState.page,
        listData.pagination?.totalPages || 0
    );

    const renderItems = useMemo(() => {
        if (!isAllTab) {
            return (listData.items || []).map((item) => ({
                item,
                isPinned: false,
            }));
        }

        const pinned = Object.values(listData.pinnedItems || [])
            .filter((entry) => entry?.item?.id != null)
            .sort((left, right) => (left.position ?? Number.MAX_SAFE_INTEGER) - (right.position ?? Number.MAX_SAFE_INTEGER))
            .map((entry) => ({
                item: entry.item,
                isPinned: true,
            }));

        const excludeIds = new Set(pinned.map((entry) => entry.item.id));
        const regular = (listData.items || [])
            .filter((item) => !excludeIds.has(item.id))
            .map((item) => ({
                item,
                isPinned: false,
            }));

        return [...pinned, ...regular];
    }, [isAllTab, listData.items, listData.pinnedItems]);

    const goToPage = (nextPage) => {
        const totalPages = listData.pagination?.totalPages || 1;
        const normalizedPage = Math.max(1, Math.min(totalPages, nextPage));
        if (normalizedPage === queryState.page) {
            return;
        }

        applyQueryState({
            ...queryState,
            page: normalizedPage,
        });
        window.scrollTo({top: 0, behavior: 'smooth'});
    };

    const buildTabLink = (path) => {
        const params = new URLSearchParams();

        if (queryState.sort !== 'popular') {
            params.set('sort', queryState.sort);
        }

        const suffix = params.toString();
        return suffix ? `${path}?${suffix}` : path;
    };

    return (
        <>
            <section>
                <div className="main-container">
                    <AdvertiseBlock unitId={topAdvertiseUnit} blockLocation="top" />

                    <div className="am-courses">
                        <header className="am-top">
                            <div className="am-top__left">
                                <h1 className="am-h1">{pageTitle}</h1>
                                <div className="am-meta">
                                    <span className="am-muted">{t(`${textNamespace}.list.found`, t('courses.list.found', 'Found'))}: </span>
                                    <strong>{totalFound}</strong>
                                </div>
                            </div>

                            <nav className="am-tabs" aria-label={t(`${textNamespace}.list.stateLabel`, t('courses.list.stateLabel', 'State'))}>
                                <NavLink className="am-tab" exact activeClassName="active" to={buildTabLink(basePath)} title={isTesting ? t('testings.tabs.all', 'All testings') : t('courses.tabs.all')}>
                                    <span>{isTesting ? t('testings.tabs.all', 'All testings') : t('courses.tabs.all')}</span>
                                    <span className="am-pill">{tabCounters.all}</span>
                                </NavLink>
                                <NavLink
                                    className={`am-tab ${!meta.loaded || meta.hasMyCourses ? '' : 'no-active-link'}`}
                                    exact
                                    activeClassName="active"
                                    to={buildTabLink(myTabPath)}
                                    title={isTesting ? t('testings.tabs.my', 'My testings') : t('courses.tabs.my')}>
                                    <span>{isTesting ? t('testings.tabs.my', 'My testings') : t('courses.tabs.my')}</span>
                                    <span className="am-pill">{tabCounters.my}</span>
                                </NavLink>
                                <NavLink className="am-tab" exact activeClassName="active" to={buildTabLink(finishedTabPath)} title={isTesting ? t('testings.tabs.finished', 'Finished') : t('courses.tabs.finished')}>
                                    <span>{isTesting ? t('testings.tabs.finished', 'Finished') : t('courses.tabs.finished')}</span>
                                    <span className="am-pill">{tabCounters.finished}</span>
                                </NavLink>
                            </nav>

                            <div className="am-top__right">
                                {isAllTab && (
                                    <button className="am-btn am-btn--ghost am-filters-btn" type="button" onClick={openFilter}>
                                        {t(`${textNamespace}.filters.open`, t('courses.filters.open', 'Filters'))}
                                    </button>
                                )}
                                <label className="am-sort" htmlFor={`sort-${isTesting ? 'testing' : 'course'}`}>
                                    <span className="am-muted">{t(`${textNamespace}.list.sortLabel`, t('courses.list.sortLabel', 'Sort'))}</span>
                                    <select
                                        id={`sort-${isTesting ? 'testing' : 'course'}`}
                                        value={queryState.sort}
                                        onChange={handleSortChange}>
                                        <option value="popular">{t(`${textNamespace}.list.sort.popular`, t('courses.list.sort.popular', 'Most popular'))}</option>
                                        <option value="newest">{t(`${textNamespace}.list.sort.newest`, t('courses.list.sort.newest', 'Newest'))}</option>
                                        <option value="score">{t(`${textNamespace}.list.sort.score`, t('courses.list.sort.score', 'By score'))}</option>
                                    </select>
                                </label>
                            </div>
                        </header>

                        <div className="am-layout no-filters">
                            {isAllTab && (
                                <>
                                    <aside className={`am-filters ${isFilterOpen ? 'is-open' : ''}`} hidden={!isFilterOpen} aria-label={t(`${textNamespace}.filters.open`, t('courses.filters.open', 'Filters'))}>
                                        <div className="am-filters__head">
                                            <strong>{t(`${textNamespace}.filters.open`, t('courses.filters.open', 'Filters'))}</strong>
                                            <button className="am-iconbtn" type="button" onClick={closeFilter}>
                                                <span>✕</span>
                                            </button>
                                        </div>

                                        <div className="am-filters__body">
                                            <div className="am-group">
                                                <div className="am-group__title">{isTesting ? t('testings.filters.category', 'Category:') : t('courses.filters.category')}</div>
                                                {(meta.categories || []).map((category) => (
                                                    <label className="am-check" key={`course-category-${category.id}`}>
                                                        <input
                                                            type="checkbox"
                                                            checked={draftFilters.categories.includes(category.id)}
                                                            onChange={() => toggleDraftArrayFilter('categories', category.id)}
                                                        />
                                                        {category.name}
                                                    </label>
                                                ))}
                                            </div>

                                            <div className="am-group">
                                                <div className="am-group__title">{isTesting ? t('testings.filters.bounty', 'Reward:') : t('courses.filters.bounty')}</div>
                                                <label className="am-check">
                                                    <input
                                                        type="checkbox"
                                                        checked={draftFilters.hasCertificate}
                                                        disabled={disableCertificateFilter}
                                                        onChange={(event) => toggleDraftHasCertificate(event.target.checked)}
                                                    />
                                                    {isTesting ? t('testings.filters.certificate', 'with certificate') : t('courses.filters.certificate')}
                                                </label>
                                            </div>

                                            <div className="am-group">
                                                <div className="am-group__title">{isTesting ? t('testings.filters.complexity.title', 'Difficulty:') : t('courses.filters.complexity.title')}</div>
                                                {[1, 2, 3].map((value) => (
                                                    <label className="am-check" key={`complexity-${value}`}>
                                                        <input
                                                            type="checkbox"
                                                            checked={draftFilters.complexity.includes(value)}
                                                            onChange={() => toggleDraftArrayFilter('complexity', value)}
                                                        />
                                                        {value === 1 && (isTesting ? t('testings.filters.complexity.low', 'low') : t('courses.filters.complexity.low'))}
                                                        {value === 2 && (isTesting ? t('testings.filters.complexity.medium', 'medium') : t('courses.filters.complexity.medium'))}
                                                        {value === 3 && (isTesting ? t('testings.filters.complexity.high', 'high') : t('courses.filters.complexity.high'))}
                                                    </label>
                                                ))}
                                            </div>

                                            {hasTypeFilter && (
                                                <div className="am-group">
                                                    <div className="am-group__title">{t('courses.filters.type.title')}</div>
                                                    <label className="am-check">
                                                        <input
                                                            type="checkbox"
                                                            checked={draftFilters.type.includes('training')}
                                                            onChange={(event) => toggleDraftType('training', event.target.checked)}
                                                        />
                                                        {t('courses.filters.type.training')}
                                                    </label>
                                                    <label className="am-check">
                                                        <input
                                                            type="checkbox"
                                                            checked={draftFilters.type.includes('commercial')}
                                                            disabled={disableCommercialType}
                                                            onChange={(event) => toggleDraftType('commercial', event.target.checked)}
                                                        />
                                                        {t('courses.filters.type.commercial')}
                                                    </label>
                                                </div>
                                            )}

                                            <div className="am-filters__actions">
                                                <button className="am-btn am-btn--primary am-btn--full" type="button" onClick={applyFilters}>
                                                    {t(`${textNamespace}.filters.apply`, t('courses.filters.apply', 'Apply'))}
                                                </button>
                                                <button className="am-btn am-btn--ghost am-btn--full" type="button" onClick={resetFilters}>
                                                    {t(`${textNamespace}.filters.reset`, t('courses.filters.reset', 'Reset'))}
                                                </button>
                                            </div>
                                        </div>
                                    </aside>

                                    <div className={`am-filters-backdrop ${isFilterOpen ? 'is-open' : ''}`} onClick={closeFilter}></div>
                                </>
                            )}

                            <main className="am-results" aria-label={t(`${textNamespace}.title`, t('courses.title'))}>
                                <div className="am-context">
                                    <div className="am-context__left">
                                        <span className="am-muted">{t(`${textNamespace}.list.stateLabel`, t('courses.list.stateLabel', 'State'))}: </span>
                                        <strong className="am-context__state">
                                            {isFinishedTab
                                                ? (isTesting ? t('testings.tabs.finished', 'Finished') : t('courses.tabs.finished'))
                                                : (tab === 'my'
                                                    ? (isTesting ? t('testings.tabs.my', 'My testings') : t('courses.tabs.my'))
                                                    : (isTesting ? t('testings.tabs.all', 'All testings') : t('courses.tabs.all')))}
                                        </strong>
                                    </div>

                                    {isAllTab && (
                                        <div className="am-context__right">
                                            <button type="button" className={`am-chip ${activeQuickChip === 'all' ? 'is-active' : ''}`} onClick={() => handleQuickChipClick('all')}>
                                                {t(`${textNamespace}.list.chips.all`, t('courses.list.chips.all', 'All'))}
                                            </button>
                                            <button type="button" className={`am-chip ${activeQuickChip === 'free' ? 'is-active' : ''}`} onClick={() => handleQuickChipClick('free')}>
                                                {t(`${textNamespace}.list.chips.free`, t('courses.list.chips.free', 'Free'))}
                                            </button>
                                            <button type="button" className={`am-chip ${activeQuickChip === 'certificate' ? 'is-active' : ''}`} onClick={() => handleQuickChipClick('certificate')}>
                                                {t(`${textNamespace}.list.chips.certificate`, t('courses.list.chips.certificate', 'With certificate'))}
                                            </button>
                                            <button type="button" className={`am-chip ${activeQuickChip === 'speciality' ? 'is-active' : ''}`} onClick={() => handleQuickChipClick('speciality')}>
                                                {t(`${textNamespace}.list.chips.speciality`, t('courses.list.chips.speciality', 'Only my specialties'))}
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {noItemsFound && (
                                    <div className="not-found-courses" dangerouslySetInnerHTML={{__html: noItemsMessage}} />
                                )}

                                {!noItemsFound && (
                                    <div className="am-grid" aria-label={t(`${textNamespace}.title`, t('courses.title'))}>
                                        {renderItems.map(({item, isPinned}) => {
                                            const Component = isAllTab ? TrainingCourseCard : UserTrainingCourseCard;

                                            return (
                                                <Component
                                                    key={`${item.id}-${isPinned ? 'pinned' : 'regular'}`}
                                                    item={item}
                                                    isPinned={isPinned}
                                                    isTesting={isTesting}
                                                    isFinishedTab={isFinishedTab}
                                                    localePrefix={localePrefix}
                                                    t={t}
                                                    onAddToProfile={handleAddToProfile}
                                                    onCertificateUnavailable={() => setShowVerifyPopup(true)}
                                                    isAddedToProfile={(id) => !!addedToProfileIds[id]}
                                                    isAddToProfilePending={(id) => !!addToProfilePendingIds[id]}
                                                />
                                            );
                                        })}
                                    </div>
                                )}

                                {(listData.pagination?.totalPages || 0) > 1 && (
                                    <div className="am-pager">
                                        <button
                                            type="button"
                                            className="am-btn"
                                            disabled={(listData.pagination?.page || queryState.page) <= 1}
                                            onClick={() => goToPage((listData.pagination?.page || queryState.page) - 1)}>
                                            {t(`${textNamespace}.list.pagination.prev`, t('courses.list.pagination.prev', 'Prev'))}
                                        </button>

                                        {paginationItems.map((entry, index) => {
                                            if (typeof entry === 'string') {
                                                return <span key={`ellipsis-${index}`} className="am-muted">…</span>;
                                            }

                                            const isActive = entry === (listData.pagination?.page || queryState.page);

                                            return (
                                                <button
                                                    type="button"
                                                    key={`page-${entry}`}
                                                    className={`am-btn ${isActive ? 'is-active' : ''}`}
                                                    onClick={() => goToPage(entry)}>
                                                    {entry}
                                                </button>
                                            );
                                        })}

                                        <button
                                            type="button"
                                            className="am-btn"
                                            disabled={(listData.pagination?.page || queryState.page) >= (listData.pagination?.totalPages || 1)}
                                            onClick={() => goToPage((listData.pagination?.page || queryState.page) + 1)}>
                                            {t(`${textNamespace}.list.pagination.next`, t('courses.list.pagination.next', 'Next'))}
                                        </button>
                                    </div>
                                )}
                            </main>
                        </div>
                    </div>

                    <AdvertiseBlock unitId={bottomAdvertiseUnit} blockLocation="bottom" />
                </div>
            </section>

            {showVerifyPopup && (
                <>
                    <div className="overlay is-visible" onClick={() => setShowVerifyPopup(false)}></div>
                    <div className="popup-fixed-wrap">
                        <div className="popup-verify">
                            <span className="close" onClick={() => setShowVerifyPopup(false)}></span>
                            <h2>{t(`${textNamespace}.finished.verifyPopup.header`, t('courses.finished.verifyPopup.header'))}</h2>
                            <ul>
                                <li>{t(`${textNamespace}.finished.verifyPopup.list.userData`, t('courses.finished.verifyPopup.list.userData'))}</li>
                                <li>{t(`${textNamespace}.finished.verifyPopup.list.diplomaNumber`, t('courses.finished.verifyPopup.list.diplomaNumber'))}</li>
                            </ul>
                            <div className="button-block">
                                <Link to="/material/1294" className="btn">{t(`${textNamespace}.finished.verifyPopup.instructions`, t('courses.finished.verifyPopup.instructions'))}</Link>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}
